import { useRing } from "../ringContext";
import { useCaps } from "../../editor/CapsProvider";
import { FreeStoneEditor } from "../FreeStoneEditor";
import { SettingIcon } from "../icons";
import { Button, Chip, Disclosure, FileButton, NumberField, OptionCard, SectionLabel, Slider, Swatch, Toggle } from "../../kit";
import { downloadJSON } from "../../../util/download";
import {
  JUNCTION_SIZE, heldSettingChips, isJunctionSetting, junctionBlockedReason, junctionSizeLabel,
  servedSettingChips, settingChipPatch, showJunctionSize,
} from "../junctionUi";
import { PatternEditor } from "../patterns/PatternEditor";
import { PatternTileIcon } from "../patterns/PatternTileIcon";
import { patternJunctionSetting, patternTilePatch, showPatternTile, withoutPatternStyle } from "../patternUi";
import type {
  ChannelCutShape, ChannelManualField, CrossChannelManualField,
  DiamondColor, DiamondPlacement, DiamondSetting, DiamondSides, DiamondStyle,
  GemUniformName, RingSpec, SideSetting, StoneShape, StoneSpacing, StoneType,
} from "../../../shared/api-contract";

// ±1, not ±0.7. `rowPosition` is normalised: the engine reads ±1 as "as far
// across the width as this stone can sit WITHOUT overhanging" (the reference's
// own law, `w/2 − d/2 − 0.42 mm`), and it clamps, so the host cannot express an
// off-band stone. Sending 0.7 therefore did not mean "Right", it meant "70% of
// the way to Right" — the row silently under-travelled and Left/Right never
// reached the edge the label promises. SIDE_POSITIONS below has always been
// ±1; this row was the odd one out, in the same file.
const POSITIONS = [
  { label: "Left", value: -1 },
  { label: "Middle", value: 0 },
  { label: "Right", value: 1 },
];

/** Side runs sit across the WALL, not across the width — same slider, other axis
 * (`sideRowPosition`, −1 inner … +1 outer). */
const SIDE_POSITIONS = [
  { label: "Inner", value: -1 },
  { label: "Middle", value: 0 },
  { label: "Outer", value: 1 },
];

const PLACEMENTS: { id: DiamondPlacement; label: string }[] = [
  { id: "top", label: "Top" },
  { id: "side", label: "Side" },
];

/** Hard-coded rather than catalog-served, exactly as `SideEdgeStyle` is: 2–4
 * fixed options the contract fixes (see the `Catalogs` note on v39). */
const SIDE_SETTINGS: { id: SideSetting; label: string }[] = [
  { id: "none", label: "None" },
  { id: "bezel", label: "Bezel" },
  { id: "channel", label: "Channel" },
  { id: "prong", label: "Prong" },
];

const SIDES: { id: DiamondSides; label: string }[] = [
  { id: "left", label: "Left" },
  { id: "right", label: "Right" },
  { id: "both", label: "Both" },
];

const STONE_TYPES: { id: StoneType; label: string }[] = [
  { id: "diamond", label: "Diamond" },
  { id: "goldBead", label: "Gold Bead" },
];

const SPANS = [
  { label: "Full", deg: 360 },
  { label: "1/2", deg: 180 },
  { label: "1/3", deg: 120 },
  { label: "1/4", deg: 90 },
];

/** The three spacings that distribute the run over a fraction of the RING and so
 * override `spreadDeg` entirely (contract: `StoneSpacing`). */
const RING_FRACTION_SPACINGS = new Set<StoneSpacing>(["half-ring", "third-ring", "full-ring"]);

/** Setting styles introduced by the Ring Builder (v37). The catalog is served by
 * the viewer so a pre-v37 viewer never lists them anyway — this set is belt and
 * braces, and it also drives the placement-law traits below. (The v39 styles —
 * Vertical / Clamp / Clamp ∠ — need no equivalent: they arrive through the same
 * catalog and are simply absent on an older viewer.) */
const V37_STYLES = new Set<DiamondStyle>([
  "eye", "cross-bezel", "cross-channel", "cross-around", "free-scatter",
]);

/**
 * Which placement controls the engine actually reads for a style
 * (`band/gemPlacement.ts` `gemList`). Only the v37 settings ever hide anything —
 * every legacy style keeps the exact control set the band wizard gave it.
 *
 * A `placement: "side"` run takes `sideRun`, which reads spread + count for any
 * style and positions the row with `sideRowPosition` instead of `rowPosition`.
 */
function traits(style: DiamondStyle, placement: DiamondPlacement) {
  const side = placement === "side";
  const cross = style === "cross-bezel" || style === "cross-channel" || style === "cross-around";
  // v40 "free-place" reads NONE of the parametric placement controls — the
  // arrangement is the position, and `gemPlacement.freeRun` runs before the
  // side branch, so it overrides `placement` too.
  const free = style === "free-place";
  // v67 "pattern" reads none of them either: every layer carries its own
  // place, span, count, spacing and setting (`BandState.stonePattern`).
  const pattern = style === "pattern";
  const fixed = free || pattern;
  return {
    /** eye / cross / scatter all sit at `placementDeg`; `spreadDeg` is never read. */
    spread: !fixed && (side || !V37_STYLES.has(style)),
    /** Cross rows and the golden-angle scatter derive `u` from the band width. */
    rowPosition: !fixed && !side && !cross && style !== "free-scatter",
    /** "eye" is always exactly one stone. */
    count: !fixed && (side || style !== "eye"),
    /** Angle, span and spacing all come from the arrangement instead. */
    free,
    /** The layers own placement, span, count, spacing and setting. */
    pattern,
  };
}

/** One sentence per region, verbatim in the reference's phrasing — noun and full
 * stop included: "62 stones on the left side." Gems and beads are tallied
 * separately by the engine (a bead is metal), so both get a clause. */
function countLine(spec: RingSpec | undefined): string | null {
  const c = spec?.stoneCounts;
  const beads = spec?.beadCount ?? 0;
  const stones = (n: number) => `${n} stone${n === 1 ? "" : "s"}`;
  const bits = [
    c?.top ? `${stones(c.top)} on top` : "",
    c?.left ? `${stones(c.left)} on the left side` : "",
    c?.right ? `${stones(c.right)} on the right side` : "",
    c?.inner ? `${stones(c.inner)} inside the band` : "",
    c?.head ? `${stones(c.head)} in the head` : "",
    beads ? `${beads} gold bead${beads > 1 ? "s" : ""}` : "",
  ].filter(Boolean);
  return bits.length ? bits.map((b) => `${b}.`).join(" ") : null;
}

/**
 * The engine's stone-fit warning (the viewer's `ringWarnings`), cut down to the
 * problem and its fix for the line under the stone count: "4 stones do not fit
 * across a 10 mm band at a 27° diagonal — they overlap by 0.48 mm; 3 fit, or
 * widen the band to 11.6 mm." reads "Too many stones — they overlap by …". Read
 * off `spec.warnings`, where the spec panel shows it in full; no RPC of its own.
 * Under an AUTOMATIC count the user picked nothing, so the line says the
 * automatic count is the one that is too full (the engine's fix then names the
 * count to set instead of Auto).
 */
function stoneFitHint(spec: RingSpec | undefined, auto: boolean): string | null {
  const w = spec?.warnings?.find((x) => /^\d+ stones do not fit\b/.test(x));
  if (!w) return null;
  const dash = w.indexOf(" — ");
  if (dash < 0) return w;
  return `${auto ? "Auto puts in too many stones for this ring" : "Too many stones"} — ${w.slice(dash + 3)}`;
}

/** The Eye setting's Orientation row. The engine's in-surface spin is `rotateZDeg`
 * (`STYLE_STONE_SPIN_DEG`, which seeds `eye: 0` on arrival) — NOT `rotateYDeg`. */
/** The two settings that hold no metal over the girdle, so they take the cut's
 *  BARE stone rather than a prong composite. `DiamondSetting` also carries
 *  "channel", which the Setting Type tiles above already own (item 7). */
/** Style tiles no longer offered on new designs (item 11). Ids stay live in the
 *  contract so saved rings keep rendering; the tile reappears only for a design
 *  that already selected it. */
const RETIRED_STYLE_TILES = new Set(["prong-4", "prong-6"]);

// The held settings (Flush · Only, and v67's Pavé · Shared prong) are listed
// in `../junctionUi` (`heldSettingChips`), which also gates the v67 pair.

const EYE_ORIENTATIONS = [
  { label: "Vertical", deg: 0 },
  { label: "Horizontal", deg: 90 },
  { label: "+45°", deg: 45 },
  { label: "-45°", deg: -45 }, // hyphen, not a typographic minus: their label verbatim
];

/**
 * Channel / Groove Cut (item 17, moved here from the Joints tab).
 *
 * WHY THESE ROWS ONCE WERE NOT CONTROLS, and what changed. `setDiamond`
 * re-derives `widthMM`, `heightMM`, `position`, `diagonalDeg`, `centerAngleDeg`
 * and `fullThrough` from the style on EVERY call, and 26 rows on this tab call
 * `setDiamond`. So a plain slider over any of them was reset by the next click
 * on anything, and they were reduced to a caption.
 *
 * v54 gives the user per-field OWNERSHIP instead (`setChannelManual`): a field
 * they touch comes off the style's re-derive list and keeps its value, and the
 * Auto button hands it back. What is deliberately NOT offered that way is
 * `position` — the trench is a shadow of the run, resolved through the stones'
 * own placement law, and `groove-coupling-check` pins that moving the row takes
 * the trench with it. Cut Position therefore still writes `rowPosition`, which
 * is what it has always really been.
 */
/** Cut Depth's ownership row, gated: a pre-v57 viewer cannot DERIVE the depth,
 *  so offering "Auto" there would promise something it will not do. A tension
 *  setting owns its depth structurally and gets neither. */
function ChannelDepthRow({ on, derived, children }: {
  on: boolean;
  derived: boolean;
  children: React.ReactNode;
}) {
  const caps = useCaps();
  if (derived || !caps.ringChannelDepthV57) return <>{children}</>;
  return <ManualRow field="depthMM" on={on}>{children}</ManualRow>;
}

function ManualRow({ field, on, children }: {
  field: ChannelManualField;
  on: boolean;
  children: React.ReactNode;
}) {
  const { r, apply } = useRing();
  return (
    <div className="flex flex-col gap-1">
      {children}
      <button
        type="button"
        // Lit while the style owns the field. Clicking it while it is NOT lit
        // hands the field back; while it IS lit there is nothing to hand back.
        //
        // ⚠️ This was `disabled={!on}`, the exact opposite of the line above and
        // of the label it renders: the button was live while it read "Auto" (and
        // did nothing, since there was nothing to hand back) and DISABLED as
        // soon as it read "Reset to auto". So the reset half of v54's whole
        // ownership feature could never be clicked — once a Cut field was
        // touched, the only way back to the derived value was to change the
        // setting type and change it back.
        disabled={on}
        onClick={() => void apply(r.setChannelManual({ [field]: null }))}
        className={`self-end font-body text-[9px] uppercase tracking-wider ${
          on ? "text-outline" : "text-primary underline underline-offset-2"
        }`}
      >
        {on ? "Auto" : "Reset to auto"}
      </button>
    </div>
  );
}

/**
 * v55 — the same Auto / "Reset to auto" affordance for the CROSS channel.
 *
 * A separate component and not a generic one because the two sets of fields
 * genuinely differ: this one has no `fullThrough` (a cross channel is never a
 * tension gap) and no `heightMM` (its length across the band is `spanMM`, a
 * different axis), and it takes ownership through `setCrossChannel` on write
 * rather than needing a separate lock call.
 */
function CrossManualRow({ field, on, children }: {
  field: CrossChannelManualField;
  on: boolean;
  children: React.ReactNode;
}) {
  const { r, apply } = useRing();
  return (
    <div className="flex flex-col gap-1">
      {children}
      <button
        type="button"
        disabled={on}
        onClick={() => void apply(r.setCrossChannelManual({ [field]: null }))}
        className={`self-end font-body text-[9px] uppercase tracking-wider ${
          on ? "text-outline" : "text-primary underline underline-offset-2"
        }`}
      >
        {on ? "Auto" : "Reset to auto"}
      </button>
    </div>
  );
}

/**
 * The cross channel's own cut rows.
 *
 * ⚠️ NOT `ChannelCutRows` with different labels. The two trenches are at right
 * angles: a channel groove runs AROUND the ring, so its Cut Width is measured
 * across the band and its Cut Length is an arc; a cross channel runs ACROSS the
 * band, so its Cut Width IS the arc and its Cut Span is the across-the-band
 * measure. Wiring one set of rows to the other would rotate every edit 90°.
 *
 * Before v55 this style had no rows at all — its whole shape was three
 * expressions inside `crossChannelCarve`.
 */
function CrossChannelCutRows() {
  const { state, r, apply } = useRing();
  const caps = useCaps();
  if (state.diamond.style !== "cross-channel" || !caps.ringCrossChannelV55) return null;
  const cc = state.crossChannel;
  if (!cc) return null;
  const owned = new Set(cc.manual ?? []);
  const set = (patch: Record<string, number | string>) => void apply(r.setCrossChannel(patch));
  // The trench's width is an ARC at the outer radius, so the ring's outer
  // circumference is its bound (the viewer clamps an owned width to it) — not
  // the band's width, which bounds the span instead. Measured off THIS ring's
  // bore: a fixed 17.3 mm made the bound wrong for every other size.
  const circumferenceMM = Math.PI * ((state.spec?.innerDiameterMM ?? 17.3) + 2 * state.thicknessMM);
  // Past 90% of the wall the cut would go through the ring; the viewer clamps
  // an owned depth there (`clampManualCrossChannel`), so the track stops there too.
  const maxDepthMM = state.thicknessMM * 0.9;
  // Position only means something once the span leaves a rail to move into.
  const room = Math.max(0, (state.widthMM - Math.min(cc.spanMM, state.widthMM)) / 2);

  return (
    <>
      <SectionLabel>Cross Channel Cut</SectionLabel>

      <div className="flex flex-col gap-1.5">
        <span className="font-body text-[10px] text-outline">Cut Shape</span>
        <div className="flex gap-2">
          {(["round", "square"] as ChannelCutShape[]).map((cs) => (
            <Chip key={cs} selected={cc.cutShape === cs} onClick={() => set({ cutShape: cs })} className="flex-1">
              {cs === "round" ? "Round (U)" : "Square (Flat)"}
            </Chip>
          ))}
        </div>
      </div>

      <CrossManualRow field="widthMM" on={!owned.has("widthMM")}>
        <Slider label="Cut Width (around)" min={0.2} max={Math.round(circumferenceMM)} step={0.05}
          value={cc.widthMM} unit="mm" onChange={(v) => set({ widthMM: v })} />
      </CrossManualRow>
      <span className="font-body text-[10px] text-outline">
        How far the trench runs AROUND the ring — the width you see from the side of the finger.
      </span>

      <CrossManualRow field="depthMM" on={!owned.has("depthMM")}>
        <Slider label="Cut Depth" min={0.02} max={maxDepthMM} step={0.01}
          value={Math.min(cc.depthMM, maxDepthMM)} unit="mm" onChange={(v) => set({ depthMM: v })} />
      </CrossManualRow>

      <CrossManualRow field="spanMM" on={!owned.has("spanMM")}>
        <Slider label="Cut Span (across)" min={0.1} max={state.widthMM} step={0.05}
          value={Math.min(cc.spanMM, state.widthMM)} unit="mm" onChange={(v) => set({ spanMM: v })} />
      </CrossManualRow>
      <span className="font-body text-[10px] text-outline">
        How far the trench reaches ACROSS the band. At the maximum it runs edge to edge, which is
        what a cross channel normally is; anything less leaves a rail on each side.
      </span>

      <CrossManualRow field="positionMM" on={!owned.has("positionMM")}>
        <Slider label="Cut Position (across)" min={-Math.max(room, 0.01)} max={Math.max(room, 0.01)} step={0.01}
          value={cc.positionMM} unit="mm" disabled={room <= 0}
          onChange={(v) => set({ positionMM: v })} />
      </CrossManualRow>
      {room <= 0 ? (
        <span className="font-body text-[10px] text-outline">
          Shorten Cut Span to move the trench across the band — one that already runs edge to edge
          has nowhere to go.
        </span>
      ) : null}

      <CrossManualRow field="centerAngleDeg" on={!owned.has("centerAngleDeg")}>
        <Slider label="Cut Centre" min={-180} max={180} step={1} value={cc.centerAngleDeg} unit="°"
          onChange={(v) => set({ centerAngleDeg: v })} />
      </CrossManualRow>
      {/* The stone frame: `CrossChannelParams.centerAngleDeg` is measured like
          `placementDeg`, 0 at 3 o'clock and −90 at the top. Auto follows the
          Placement slider above; the stones sit in the trench, so moving the
          cut carries them with it. */}
      <span className="font-body text-[10px] text-outline">
        Where round the ring the trench sits: −90° is the top (12 o&rsquo;clock), 0° the right-hand side,
        90° the bottom. Auto follows Placement; the stones move with the trench.
      </span>

      <CrossManualRow field="diagonalDeg" on={!owned.has("diagonalDeg")}>
        <Slider label="Cut Diagonal" min={-80} max={80} step={1} value={cc.diagonalDeg} unit="°"
          onChange={(v) => set({ diagonalDeg: v })} />
      </CrossManualRow>
      <span className="font-body text-[10px] text-outline">
        Tilts the trench so it crosses the band at an angle instead of straight across the finger. The
        stones stay on the trench&rsquo;s centre line, spaced along it.
      </span>
    </>
  );
}

function ChannelCutRows() {
  const { state, r, apply } = useRing();
  const caps = useCaps();
  const ch = state.channel;
  const d = state.diamond;
  const derivedDepth = d.style === "tension-1" || d.style === "tension-2";
  const owned = new Set(ch.manual ?? []);
  const manual = caps.ringChannelManualV54;
  // `heightMM` past the circumference means "all the way round" — that is what
  // the 100 sentinel every style writes is saying. Slider to the real bound.
  const circumferenceMM = Math.PI * (state.ringSizeUS ? 17.3 + 2 * state.thicknessMM : 20.9);
  const set = (patch: Record<string, number | boolean>) => void apply(r.setChannelManual(patch));
  /** The cut goes all the way round — Centre and Diagonal have nothing to act on. */
  const arcFull = ch.fullThrough || ch.heightMM >= circumferenceMM * 0.95;

  // ITEM 8 — Square vs Round channel, gated on the ENGINE's own truth
  // (`channel.enabled`, set from `info.needsGroove`) instead of the hard-coded
  // style list this used to carry. That list had gone stale: it never included
  // `vertical`, which v44 gave `needsGroove: true`, so a Vertical row milled a
  // trench the user could not choose the floor shape of.
  if (!ch.enabled) return null;

  return (
    <>
      <SectionLabel>Channel / Groove Cut</SectionLabel>

      <div className="flex flex-col gap-1.5">
        <span className="font-body text-[10px] text-outline">Cut Shape</span>
        <div className="flex gap-2">
          {(["round", "square"] as ChannelCutShape[]).map((cs) => (
            <Chip
              key={cs}
              selected={ch.cutShape === cs}
              onClick={() => void apply(r.setChannelCutShape(cs))}
              className="flex-1"
            >
              {cs === "round" ? "Round (U)" : "Square (Flat)"}
            </Chip>
          ))}
        </div>
      </div>

      {/* v57: the depth is ownable, so it can be DERIVED from the stone
          (0.30 d, measured off the reference) instead of sitting at a flat
          0.6 mm. Without the ownership row the derivation would silently
          overwrite whatever the user set, on the next `setDiamond` — which is
          why the contract change came first. Same `ManualRow` the other three
          channel dimensions use, and the cross-channel panel already used for
          its own depth. */}
      <ChannelDepthRow on={!owned.has("depthMM") && !derivedDepth} derived={derivedDepth}>
        <Slider
          label="Cut Depth"
          min={0.1}
          max={2}
          step={0.05}
          value={ch.depthMM}
          unit="mm"
          disabled={derivedDepth}
          onChange={(v) => void apply(r.setChannelManual({ depthMM: v }))}
        />
      </ChannelDepthRow>
      {derivedDepth ? (
        <span className="font-body text-[10px] text-outline">
          A tension setting sets its own cut depth from the band thickness.
        </span>
      ) : null}

      <Slider
        label="Cut Position"
        min={-1}
        max={1}
        step={0.05}
        value={d.rowPosition}
        onChange={(v) => void r.setDiamond({ rowPosition: v })}
      />

      {manual ? (
        <>
          <ManualRow field="widthMM" on={!owned.has("widthMM")}>
            <Slider
              label="Cut Width"
              min={0.2}
              max={Math.max(2, state.widthMM)}
              step={0.01}
              value={ch.widthMM}
              unit="mm"
              onChange={(v) => set({ widthMM: v })}
            />
          </ManualRow>

          <ManualRow field="heightMM" on={!owned.has("heightMM")}>
            <Slider
              label="Cut Length (around)"
              min={0.5}
              max={Math.round(circumferenceMM)}
              step={0.5}
              value={Math.min(ch.heightMM, Math.round(circumferenceMM))}
              unit="mm"
              disabled={ch.fullThrough}
              onChange={(v) => set({ heightMM: v })}
            />
          </ManualRow>
          <span className="font-body text-[10px] text-outline">
            How far the cut runs AROUND the ring, in mm of arc. At the maximum it goes all the
            way round.
          </span>

          <ManualRow field="centerAngleDeg" on={!owned.has("centerAngleDeg")}>
            <Slider
              label="Cut Centre"
              min={0}
              max={360}
              step={1}
              value={ch.centerAngleDeg}
              unit="°"
              disabled={arcFull}
              onChange={(v) => set({ centerAngleDeg: v })}
            />
          </ManualRow>

          <ManualRow field="diagonalDeg" on={!owned.has("diagonalDeg")}>
            <Slider
              label="Cut Diagonal"
              min={-90}
              max={90}
              step={1}
              value={ch.diagonalDeg}
              unit="°"
              // Same gate as Cut Centre, for a geometric reason: a cut that runs
              // ALL the way round cannot also be tilted — it would be a helix,
              // and a helix does not meet itself after 360°. Shorten the cut
              // first and the diagonal becomes meaningful.
              disabled={arcFull}
              onChange={(v) => set({ diagonalDeg: v })}
            />
          </ManualRow>
          {arcFull && ch.diagonalDeg !== 0 ? (
            <span className="font-body text-[10px] text-outline">
              Shorten Cut Length to angle the cut — a full-circle groove cannot be tilted.
            </span>
          ) : null}

          {/* Cut Through is DERIVED, and deliberately not ownable — it is what
              MAKES a tension setting (it splits the ring so the stone can float
              in a real gap), not a dimension of the cut. v54 briefly let a user
              take it over, and one click here permanently turned every tension
              setting into a solid ring with a bridge over unbroken metal. Shown
              as the state it is, so the panel still tells the truth about the
              geometry. */}
          <div className="flex items-center justify-between gap-3 opacity-60">
            <span className="flex flex-col">
              <span className="font-display text-[11px] font-medium uppercase tracking-[0.12em] text-on-surface-variant">
                Cut Through
              </span>
              <span className="font-body text-[10px] text-outline">
                {ch.fullThrough
                  ? "This setting cuts the band right through — that gap is what holds the stone."
                  : "Set by the setting type. Tension settings cut the band through."}
              </span>
            </span>
            <Toggle label="Cut through both surfaces" checked={ch.fullThrough} onChange={() => {}} />
          </div>
        </>
      ) : (
        <span className="font-body text-[10px] tabular-nums text-outline">
          Cut {ch.widthMM.toFixed(2)} mm wide{ch.diagonalDeg ? `, ${ch.diagonalDeg}° diagonal` : ""} —
          sized from the stone and its row.
        </span>
      )}
    </>
  );
}

/**
 * Ring Builder › Gems. The band Diamonds step (including the channel/groove cut
 * controls, which moved here in item 17), the v37 setting styles and the whole
 * v39 diamond vocabulary — placement, side hardware, spacing, colour, beads.
 */
export function GemsTab() {
  const { state, catalogs, r, apply } = useRing();
  const caps = useCaps();
  const d = state.diamond;
  const v39 = caps.ringDiamondsV39;
  const placement: DiamondPlacement = (v39 && d.placement) || "top";
  const bead = d.type === "goldBead";
  const info = catalogs.diamondStyles.find((s) => s.id === d.style) ?? catalogs.diamondStyles[0];
  const styles = (caps.ringBuilderV37
    ? catalogs.diamondStyles
    : catalogs.diamondStyles.filter((s) => !V37_STYLES.has(s.id))
  // Item 11 — the "4-Prong setting" / "6-Prong setting" tiles are retired.
  // Prong choice now lives on the Diamond Setting axis, where each chip is a
  // real modelled gallery style rather than a claw COUNT the band builder never
  // varied: the two tiles are byte-identical in DIAMOND_STYLE_INFO apart from
  // their label, and their single consumer is one `usesProng` test.
  // HIDDEN, not deleted, and still shown when a design already has one selected
  // — same rule as Gold Bead. Retiring the ids would resolve them to `none` and
  // strip the stones off every saved ring that used them. (Note these are the
  // BAND's tiles; the engagement head's real 4/6/8-prong control is a different
  // catalogue on the Engagement tab and is untouched.)
  ).filter((s) => !RETIRED_STYLE_TILES.has(s.id) || d.style === s.id);
  // v50 two-axis gem picker. `stoneCuts` carries a resolved shape id per cell,
  // so nothing here parses or builds an id: an absent key IS the "not modelled"
  // signal and the chip is disabled. Falls back to the flat 48-tile list when
  // the viewer predates v50 and omits the catalogs entirely.
  const gemMatrix = caps.ringGemMatrixV50 && (catalogs.stoneCuts?.length ?? 0) > 0;
  const cut = (catalogs.stoneCuts ?? []).find(
    (c) => c.bare === d.stoneShape || Object.values(c.shapes).includes(d.stoneShape),
  );
  const curVariant = cut
    ? Object.keys(cut.shapes).find((v) => cut.shapes[v] === d.stoneShape) ?? null
    : null;
  // Keep the catalogue's own variant ORDER (prong, claw, square, heart, leaf)
  // rather than the per-cut key order, so the row does not reshuffle as the
  // user moves between cuts with different holes in the matrix.
  const prongChips = (catalogs.prongVariants ?? []).filter((v) =>
    (catalogs.stoneCuts ?? []).some((c) => c.shapes[v.id]),
  );
  // (A `pronglessSetting` flag used to live here, to suppress the prong
  // selection while Flush or Only was active. With the two axes split it is not
  // needed: `curVariant` alone says which prong is on, and the interlock — Flush
  // and Only require Prong: None — is expressed on the chips themselves.)

  const t = traits(d.style, placement);
  /** `freeRun` runs before the side branch in `gemPlacement.mainRun`, so a free
   * arrangement is never a side run however `placement` was left. */
  const side = placement === "side" && !t.free && !t.pattern;
  const spanLocked = v39 && d.spacing !== undefined && RING_FRACTION_SPACINGS.has(d.spacing);
  const counts = v39 && !t.free ? countLine(state.spec) : null;
  const fitHint = stoneFitHint(state.spec, !(d.count > 0));
  /** The v40 free editor owns the whole count/angle/span story when it is up. */
  const freeEditor = t.free && caps.ringFreeStonesV40;
  /** v67: a pattern layer set Pavé / Shared claws reads the same bead/claw size. */
  const patternJunction = caps.ringDesignV67 ? patternJunctionSetting(state) : null;

  /** Cube law off the engine's own figure, so the shape factor never has to be
   * duplicated here and the readout stays live mid-drag. */
  const caratAt = (mm: number) => {
    const base = state.spec?.perStoneCarat ?? 0;
    return base > 0 && d.stoneSizeMM > 0 ? base * (mm / d.stoneSizeMM) ** 3 : (mm / 6.5) ** 3;
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Free placement is an unrolled view of the OUTER surface — `placement`
          has nothing to say about it, so the chips would be a dead control. */}
      {v39 && !t.free && !t.pattern ? (
        <div className="flex flex-col gap-2">
          <SectionLabel>Placement</SectionLabel>
          <div className="flex gap-2">
            {PLACEMENTS.map((p) => (
              <Chip
                key={p.id}
                selected={placement === p.id}
                onClick={() => void apply(r.setDiamond(
                  // Picking Side on a ring that has no stones yet used to do
                  // NOTHING, for every sub-option — `gemPlacement.gemList` bails
                  // on `style: "none"` BEFORE it ever reaches the side branch,
                  // and "none" is the shipping default. So the first thing a
                  // user does on a fresh ring is click a control that cannot
                  // respond. Bring a run with it; the Stones chips below still
                  // own the choice afterwards.
                  p.id === "side" && d.style === "none"
                    ? { placement: p.id, style: "vertical", count: 0, spreadDeg: 360 }
                    : { placement: p.id },
                ))}
                className="flex-1"
              >
                {p.label}
              </Chip>
            ))}
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        <SectionLabel>{side ? "Stones" : "Setting Type"}</SectionLabel>
        {side ? (
          // A side run's look comes from Setting Type below; the style only
          // decides whether there is a run at all (`gemPlacement.sideRun`).
          <div className="flex gap-2">
            {[{ id: "none", label: "None" }, { id: "vertical", label: "Stones" }].map((s) => (
              <Chip
                key={s.id}
                selected={(d.style === "none") === (s.id === "none")}
                onClick={() => void apply(r.setDiamond({ style: s.id as DiamondStyle }))}
                className="flex-1"
              >
                {s.label}
              </Chip>
            ))}
          </div>
        ) : (
          // Icon tiles, each DRAWN from the two numbers the tile applies —
          // `defaultCount` and `defaultSpreadDeg` off the served catalog entry
          // (see `stoneLayout` in ../icons). A style added to the engine gets a
          // correct glyph with no work here, and no glyph can promise a layout
          // the setter does not produce.
          <div className="grid grid-cols-3 gap-2">
            {withoutPatternStyle(styles).map((si) => (
              <OptionCard
                key={si.id}
                title={si.label}
                selected={d.style === si.id}
                onClick={() =>
                  void apply(r.setDiamond({ style: si.id, count: si.defaultCount, spreadDeg: si.defaultSpreadDeg }))
                }
              >
                <SettingIcon style={si.id} count={si.defaultCount} spreadDeg={si.defaultSpreadDeg} />
                <span className="w-full truncate text-center font-display text-[8px] uppercase tracking-[0.08em] text-on-surface-variant">
                  {si.label}
                </span>
              </OptionCard>
            ))}
            {/* v67: the stone PATTERN tile — drawn here rather than served,
                and only on a viewer that reads `stonePattern` (an older one
                would store the id and draw nothing). Switching to it keeps a
                pattern the ring already carries, else seeds layer 1 from the
                row (`patternTilePatch`). */}
            {showPatternTile(caps) ? (
              <OptionCard
                title="Pattern"
                selected={t.pattern}
                onClick={() => { if (!t.pattern) void apply(r.applyRingDesign(patternTilePatch(state))); }}
              >
                <PatternTileIcon />
                <span className="w-full truncate text-center font-display text-[8px] uppercase tracking-[0.08em] text-on-surface-variant">
                  Pattern
                </span>
              </OptionCard>
            ) : null}
          </div>
        )}
      </div>

      {freeEditor ? <FreeStoneEditor /> : null}
      {t.pattern && showPatternTile(caps) ? <PatternEditor /> : null}

      {d.style !== "none" ? (
        <>
          {side ? (
            <>
              <div className="flex flex-col gap-2">
                <SectionLabel>Setting Type</SectionLabel>
                <div className="flex gap-2">
                  {SIDE_SETTINGS.map((s) => (
                    <Chip
                      key={s.id}
                      selected={(d.sideSetting ?? "bezel") === s.id}
                      onClick={() => void apply(r.setDiamond({ sideSetting: s.id }))}
                      className="flex-1"
                    >
                      {s.label}
                    </Chip>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <SectionLabel>Sides</SectionLabel>
                <div className="flex gap-2">
                  {SIDES.map((s) => (
                    <Chip
                      key={s.id}
                      selected={(d.sides ?? "both") === s.id}
                      onClick={() => void apply(r.setDiamond({ sides: s.id }))}
                      className="flex-1"
                    >
                      {s.label}
                    </Chip>
                  ))}
                </div>
              </div>
            </>
          ) : gemMatrix ? (
            <>
              {/* TWO AXES, not one row of seven chips.
                  "Diamond Setting: Flush or Only, then prong: None, and Prong /
                  Claw / Square / Heart / Leaf" — they are orthogonal questions.
                  How the stone is HELD is the setting; WHICH gallery asset sits
                  under it is the prong. Mixing them into one row made "Flush"
                  look like a sixth prong style, which it is not: it is the
                  absence of one.

                  Note this is our own vocabulary, not the reference's. iJewel3D
                  has no prong axis and no stone-shape axis at all — measured
                  live: their Diamond Setting is Channel / Prongs / Flush / Only
                  and clicking "Prongs" reveals nothing further. The 43-GLB
                  matrix is ours. */}
              {/* A pattern sets each LAYER's stones its own way (the layer
                  card's Setting row), so the run's chips would be dead here. */}
              {t.pattern ? null : (
              <div className="flex flex-col gap-2">
                <SectionLabel>Diamond Setting</SectionLabel>
                <div className="flex flex-wrap gap-2">
                  {/* The settings that hold NO metal over the girdle, so they
                      take the bare cut rather than a composite. Both are
                      TOGGLES: clicking the active chip returns `setting` to
                      `undefined`, the plain seat a design that never touched
                      this row has. Without that there is no way back, because
                      there is no third "neither" chip to click. */}
                  {/* v67 adds Pavé (beads shared between neighbours) and Shared
                      prong (one claw between two stones). They hold no metal of
                      their own over the girdle either — the junction metal is
                      built between the stones — so they follow the same
                      bare-cut rule, and they are dead on a cross-channel or
                      tension row (the trench / the split band holds those). */}
                  {heldSettingChips(caps).map((ps) => {
                    const on = d.setting === ps.id;
                    const deadFor = isJunctionSetting(ps.id) ? junctionBlockedReason(d.style) : null;
                    const blocked = !cut?.bare || (!on && curVariant != null) || (!on && deadFor != null);
                    return (
                      <Chip
                        key={ps.id}
                        selected={on}
                        disabled={blocked}
                        title={
                          !cut?.bare
                            ? `${ps.label} needs a prong-less ${cut?.label ?? "stone"}, which is not in the library yet`
                            : curVariant != null && !on
                              ? `${ps.label} sets the stone into the metal, so it cannot carry a ${curVariant} head — set Prong to None first`
                              : deadFor && !on
                                ? deadFor
                                : undefined
                        }
                        onClick={() => {
                          if (blocked) return;
                          void apply(r.setDiamond(settingChipPatch(ps.id, on, cut!.bare! as StoneShape)));
                        }}
                        className="flex-1"
                      >
                        {ps.label}
                      </Chip>
                    );
                  })}
                </div>
                {cut && !cut.bare ? (
                  <span className="font-body text-[10px] text-outline">
                    {caps.ringDesignV67 ? "Flush, Only, Pavé and Shared prong" : "Flush and Only"} need a
                    prong-less {cut.label.toLowerCase()}, which is not in the library yet.
                  </span>
                ) : null}
              </div>
              )}

              <div className="flex flex-col gap-2">
                <SectionLabel>Prong</SectionLabel>
                <div className="flex flex-wrap gap-2">
                  {/* None = the bare cut. It is a real choice, not the absence
                      of one, and it is what Flush and Only sit on top of. */}
                  <Chip
                    selected={curVariant == null}
                    disabled={!cut?.bare}
                    title={cut?.bare ? undefined : `No prong-less ${cut?.label ?? "stone"} is in the library yet`}
                    onClick={() => {
                      if (!cut?.bare) return;
                      // `setting` is deliberately untouched: a bare stone can
                      // still be Flush or Only, and those chips above own it.
                      void apply(r.setStoneShape(cut.bare));
                    }}
                    className="flex-1"
                  >
                    None
                  </Chip>
                  {prongChips.map((v) => {
                    const target = cut?.shapes[v.id];
                    return (
                      <Chip
                        key={v.id}
                        selected={curVariant === v.id}
                        disabled={!target}
                        title={target ? undefined : `No ${v.label.toLowerCase()} setting is modelled for ${cut?.label ?? "this cut"}`}
                        onClick={() => {
                          if (!target) return;
                          // Clear `setting` — a stone under a gallery head is
                          // not flush-set — but write NOTHING else. Writing
                          // `setting: "prongs"` here looked natural and was
                          // wrong: `resolveStyleInfo` reads that as a HARDWARE
                          // law and forces `needsGroove: false`, so picking a
                          // gallery style on a Channel row silently switched the
                          // milled trench off. The Setting Type tile above still
                          // owns how the stone is held.
                          void apply(r.setDiamond({ setting: undefined, stoneShape: target }));
                        }}
                        className="flex-1"
                      >
                        {v.label}
                      </Chip>
                    );
                  })}
                </div>
              </div>
            </>
          ) : v39 && (catalogs.diamondSettings?.length ?? 0) > 0 ? (
            <div className="flex flex-col gap-2">
              <SectionLabel>Diamond Setting</SectionLabel>
              <div className="flex flex-wrap gap-2">
                {/* Pre-v50 fallback: a viewer that does not serve the matrix
                    still gets the flat setting list it has always had. */}
                <Chip
                  selected={d.setting === undefined}
                  onClick={() => void apply(r.setDiamond({ setting: undefined }))}
                  className="flex-1"
                >
                  Auto
                </Chip>
                {servedSettingChips(catalogs.diamondSettings ?? [], caps).map((s) => (
                  <Chip
                    key={s.id}
                    selected={d.setting === s.id}
                    onClick={() => void apply(r.setDiamond({ setting: s.id as DiamondSetting }))}
                    className="flex-1"
                  >
                    {s.label}
                  </Chip>
                ))}
              </div>
            </div>
          ) : null}

          {/* v67: how big the shared beads / claws are, as a multiple of the
              engine's own law (sized from the two neighbouring stones). Only
              the two junction settings read it. Kit slider ⇒ draft-batched. */}
          {showJunctionSize(caps, d.setting, side) || patternJunction ? (
            <Slider
              label={junctionSizeLabel(patternJunction ?? d.setting)}
              min={JUNCTION_SIZE.min}
              max={JUNCTION_SIZE.max}
              step={JUNCTION_SIZE.step}
              value={d.junctionScale ?? 1}
              display={(v) => `${Math.round(v * 100)}%`}
              minLabel="Smaller"
              maxLabel="Larger"
              onChange={(v) => void r.setDiamond({ junctionScale: v })}
            />
          ) : null}

          <div className="flex flex-col gap-2">
            <SectionLabel>Stone Shape</SectionLabel>
            {gemMatrix ? (
              /* THE CUT AXIS: ten cuts, which is exactly the list the assets
                 already hold. This row used to carry all 48 catalog entries —
                 the cartesian product of these ten with the five gallery styles,
                 flattened — so choosing "Claw" meant finding the tile that
                 happened to pair Claw with the cut you already had.

                 Changing cut KEEPS the current setting where that cell exists;
                 where it does not (seven cells were never modelled) it falls
                 back to the cut's plain Prong, and to its bare stone if even
                 that is missing. It never silently lands on another cut. */
              <div className="grid grid-cols-5 gap-2">
                {(catalogs.stoneCuts ?? []).map((c) => {
                  const keep = curVariant && c.shapes[curVariant];
                  const target =
                    (d.setting === "flush" || d.setting === "only" || isJunctionSetting(d.setting) ? c.bare : null) ??
                    keep ?? c.shapes.prong ?? Object.values(c.shapes)[0] ?? c.bare;
                  return (
                    <Chip
                      key={c.id}
                      selected={cut?.id === c.id}
                      disabled={!target}
                      title={target ? undefined : `No ${c.label} stone is available for the current setting`}
                      onClick={() => { if (target) void apply(r.setStoneShape(target as StoneShape)); }}
                    >
                      {c.label}
                    </Chip>
                  );
                })}
              </div>
            ) : (
              <div className="grid grid-cols-5 gap-2">
                {catalogs.stoneShapes.map((sh) => (
                  <Chip
                    key={sh.id}
                    selected={d.stoneShape === sh.id}
                    onClick={() => void apply(r.setStoneShape(sh.id as StoneShape))}
                  >
                    {sh.label}
                  </Chip>
                ))}
              </div>
            )}
          </div>

          {/* Item 16 — Gold Bead is retired from the picker.
              HIDDEN, not deleted. `goldBead` stays a live StoneType in the
              contract and the engine keeps its bead path, so a design already
              saved with beads still renders — and still PRICES — exactly as it
              was saved. Removing the id instead would have re-rendered those
              rings as diamonds and moved their quote, which is a bigger change
              than "take the option off the menu".
              The row therefore appears only for a design that already carries
              beads, purely so it can be switched back; a diamond run never sees
              it, and once switched it does not come back. */}
          {v39 && d.type === "goldBead" ? (
            <div className="flex flex-col gap-2">
              <SectionLabel>Type</SectionLabel>
              <div className="flex gap-2">
                {STONE_TYPES.map((s) => (
                  <Chip
                    key={s.id}
                    selected={(d.type ?? "diamond") === s.id}
                    onClick={() => void apply(r.setDiamond({ type: s.id }))}
                    className="flex-1"
                  >
                    {s.label}
                  </Chip>
                ))}
              </div>
              <span className="font-body text-[10px] text-outline">
                Gold beads are no longer offered on new designs. Switching to Diamond is
                permanent for this ring.
              </span>
            </div>
          ) : null}

          {/* Eye is a single stone whose only per-style control is how it sits in
              the surface. Four presets over the same `rotateZDeg` the Rotate Z
              slider below writes, so they stay in sync by construction. */}
          {d.style === "eye" && !side ? (
            <div className="flex flex-col gap-2">
              <SectionLabel>Orientation</SectionLabel>
              <div className="flex gap-2">
                {EYE_ORIENTATIONS.map((o) => (
                  <Chip
                    key={o.label}
                    selected={Math.abs((d.rotateZDeg ?? 0) - o.deg) < 1}
                    onClick={() => void apply(r.setDiamond({ rotateZDeg: o.deg }))}
                    className="flex-1"
                  >
                    {o.label}
                  </Chip>
                ))}
              </div>
            </div>
          ) : null}

          {/* A gold bead is metal — it takes the band material, so the gem colour
              and the gem shader panel have nothing to act on. */}
          {v39 && !bead && (catalogs.diamondColors?.length ?? 0) > 0 ? (
            <div className="flex flex-col gap-2">
              <SectionLabel>Diamond Colour</SectionLabel>
              <div className="flex flex-wrap gap-3">
                {(catalogs.diamondColors ?? []).map((c) => (
                  <Swatch
                    key={c.id}
                    color={parseInt(c.swatch.replace("#", ""), 16)}
                    label={c.label}
                    // Read THROUGH the default rather than writing it: the default
                    // state is already a white stone (`gemPresetId: diamondClear`),
                    // so White must read as selected on arrival.
                    selected={(d.color ?? "white") === c.id}
                    onClick={() => void apply(r.setDiamond({ color: c.id as DiamondColor }))}
                  />
                ))}
              </div>
            </div>
          ) : null}

          {bead ? null : <GemMaterialPanel />}

          <Slider label="Stone Size" min={0.5} max={4} step={0.1} value={d.stoneSizeMM} unit="mm"
            display={bead ? undefined : (v) => `${v.toFixed(2)} mm · ${caratAt(v).toFixed(3)} ct`}
            onChange={(v) => void r.setDiamond({ stoneSizeMM: v })} />
          {t.count ? (
            <Slider label="Number of Stones" min={0} max={60} step={1} value={d.count}
              display={(v) => (v === 0 ? "Auto" : String(Math.round(v)))}
              onChange={(v) => void r.setDiamond({ count: Math.round(v) })} />
          ) : null}
          {/* The engine keeps the count it was given and squeezes the row to
              fit, so the count alone never says the stones collide. */}
          {t.count && fitHint ? (
            <span className="-mt-3 font-body text-[10px] leading-snug text-amber-800">{fitHint}</span>
          ) : null}

          {v39 && !t.free && !t.pattern && (catalogs.stoneSpacings?.length ?? 0) > 0 ? (
            <div className="flex flex-col gap-2">
              <SectionLabel>Spacing</SectionLabel>
              <div className="flex flex-wrap gap-2">
                <Chip selected={d.spacing === undefined} onClick={() => void apply(r.setDiamond({ spacing: undefined }))}>
                  Default
                </Chip>
                {(catalogs.stoneSpacings ?? []).map((s) => (
                  <Chip
                    key={s.id}
                    selected={d.spacing === s.id}
                    onClick={() => void apply(r.setDiamond({ spacing: s.id as StoneSpacing }))}
                  >
                    {s.label}
                  </Chip>
                ))}
              </div>
            </div>
          ) : null}

          {t.rowPosition || side ? (
            <>
              <div className="flex flex-col gap-2">
                <SectionLabel>Position</SectionLabel>
                <div className="flex gap-2">
                  {(() => {
                    // NEAREST chip wins, rather than a fixed ±0.15 window.
                    // The window left the row with NOTHING lit for any value it
                    // did not bracket — which now includes every design saved
                    // against the old ±0.7 chips, and every value the Free
                    // Position slider below can produce (it steps 0.05 across
                    // the whole range). A three-way radio must always show the
                    // user which way the run is biased.
                    const list = side ? SIDE_POSITIONS : POSITIONS;
                    const cur = side ? d.sideRowPosition ?? 0 : d.rowPosition;
                    let best = 0;
                    for (let i = 1; i < list.length; i++) {
                      if (Math.abs(list[i].value - cur) < Math.abs(list[best].value - cur)) best = i;
                    }
                    return list.map((pos, i) => (
                      <Chip
                        key={pos.label}
                        selected={i === best}
                        onClick={() =>
                          void apply(side ? r.setDiamond({ sideRowPosition: pos.value }) : r.setDiamond({ rowPosition: pos.value }))
                        }
                        className="flex-1"
                      >
                        {pos.label}
                      </Chip>
                    ));
                  })()}
                </div>
              </div>

              <Slider label="Free Position" min={-1} max={1} step={0.05}
                value={side ? d.sideRowPosition ?? 0 : d.rowPosition}
                onChange={(v) => void r.setDiamond(side ? { sideRowPosition: v } : { rowPosition: v })} />
            </>
          ) : null}

          {t.free || t.pattern ? null : (
            <Slider label="Placement" min={-180} max={180} step={1} value={d.placementDeg} unit="°"
              onChange={(v) => void r.setDiamond({ placementDeg: v })} />
          )}
          {/* `defaultCount === 1` styles are single-stone on the outer surface;
              a side run is always a run, so it keeps its arc controls. */}
          {(side || info.defaultCount !== 1) && t.spread ? (
            spanLocked ? (
              <span className="font-body text-[10px] text-outline">
                Span is set by the “{(catalogs.stoneSpacings ?? []).find((s) => s.id === d.spacing)?.label}” spacing.
              </span>
            ) : (
              <>
                {v39 ? (
                  <div className="flex flex-col gap-2">
                    <SectionLabel>Span</SectionLabel>
                    <div className="flex gap-2">
                      {SPANS.map((s) => (
                        <Chip
                          key={s.label}
                          selected={Math.abs(d.spreadDeg - s.deg) < 1}
                          onClick={() => void apply(r.setDiamond({ spreadDeg: s.deg }))}
                          className="flex-1"
                        >
                          {s.label}
                        </Chip>
                      ))}
                    </div>
                  </div>
                ) : null}
                <Slider label="Spread" min={0} max={360} step={5} value={d.spreadDeg} unit="°"
                  onChange={(v) => void r.setDiamond({ spreadDeg: v })} />
              </>
            )
          ) : null}

          {counts ? (
            <span className="font-body text-[11px] tabular-nums text-on-surface-variant">{counts}</span>
          ) : null}

          {/* ITEM 17 — the channel/groove cut belongs beside the stones that sit
              in it. It lived on the Joints tab, two tabs away from the only
              decision that turns it on. */}
          <ChannelCutRows />
          <CrossChannelCutRows />

          <SectionLabel>Stone Orientation &amp; Depth</SectionLabel>
          <Slider label="Depth Offset" min={-2} max={2} step={0.05} value={d.depthOffsetMM ?? 0} unit="mm"
            onChange={(v) => void r.setDiamond({ depthOffsetMM: v })} />
          <Slider label="Rotate X" min={-180} max={180} step={1} value={d.rotateXDeg ?? 0} unit="°"
            onChange={(v) => void r.setDiamond({ rotateXDeg: v })} />
          <Slider label="Rotate Y" min={-180} max={180} step={1} value={d.rotateYDeg ?? 0} unit="°"
            onChange={(v) => void r.setDiamond({ rotateYDeg: v })} />
          <Slider label="Rotate Z" min={-180} max={180} step={1} value={d.rotateZDeg ?? 0} unit="°"
            onChange={(v) => void r.setDiamond({ rotateZDeg: v })} />
        </>
      ) : null}
    </div>
  );
}

/** Baseline gem uniform values (DIAMOND_PRESETS.diamondClear) shown until an
 * override is set. State is the source of truth once a slider is touched. */
const GEM_SCALAR_BASELINE: Record<string, number> = {
  bounces: 8, ior: 2.42, dispersion: 0.005, reflectivity: 0.4, envMapIntensity: 2.2,
  sparkleBoost: 1.0, absorptionFactor: 0.25, gammaFactor: 2.1, squashFactor: 0.98, geometryFactor: 0.5,
  opacity: 1,
};
const GEM_BOOST_BASELINE: [number, number, number] = [1.15, 1.15, 1.15];
const GEM_COLOR_BASELINE: [number, number, number] = [1, 1, 1];

const GEM_UNIFORMS: { name: GemUniformName; label: string; min: number; max: number; step: number }[] = [
  { name: "bounces", label: "Bounces", min: 1, max: 16, step: 1 },
  { name: "ior", label: "IOR", min: 1.0, max: 3.0, step: 0.00001 },
  { name: "dispersion", label: "Dispersion", min: 0, max: 0.15, step: 0.00001 },
  { name: "reflectivity", label: "Reflectivity", min: 0, max: 1, step: 0.00001 },
  { name: "envMapIntensity", label: "Env Intensity", min: 0, max: 5, step: 0.00001 },
  { name: "sparkleBoost", label: "Sparkle Boost", min: 0.5, max: 2.0, step: 0.00001 },
  { name: "absorptionFactor", label: "Absorption", min: 0, max: 3, step: 0.00001 },
  { name: "gammaFactor", label: "Gamma", min: 0.1, max: 3.0, step: 0.00001 },
  { name: "squashFactor", label: "Squash Factor", min: 0.1, max: 2.0, step: 0.00001 },
  { name: "geometryFactor", label: "Geometry Factor", min: 0, max: 1, step: 0.00001 },
];

function GemMaterialPanel() {
  const { state, catalogs, r, apply } = useRing();
  const g = state.diamond.gemParams ?? {};

  const handleImport = (file: File) => {
    void file.text().then((text) => apply(r.importBandDiamondJSON(text)));
  };
  const handleExport = async () => {
    const json = await r.exportBandDiamondJSON();
    if (json) downloadJSON(json, "diamond-material.json");
  };

  const vec = (field: "boostFactors" | "color", comp: "x" | "y" | "z") => (v: number) =>
    void apply(r.setGemVec3Component(field, comp, v));

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-outline-variant/40 bg-surface-container-lowest p-4">
      <SectionLabel>Gem Material</SectionLabel>
      <div className="flex flex-wrap gap-2">
        {catalogs.gemPresets.map((gp) => (
          <Chip
            key={gp.id}
            selected={state.diamond.gemPresetId === gp.id}
            onClick={() => void apply(r.setGemPreset(gp.id))}
          >
            {gp.label}
          </Chip>
        ))}
      </div>

      {/* `draft={false}` throughout: these are shader uniforms, not geometry.
          endDraft() forces a full hi-res band rebuild, which a colour tweak
          must not pay for. */}
      <Disclosure
        label="Advanced"
        value={Object.keys(g).length ? `${Object.keys(g).length} overridden` : "Default"}
      >
        {GEM_UNIFORMS.map((p) => (
          <NumberField
            key={p.name}
            label={p.label}
            min={p.min}
            max={p.max}
            step={p.step}
            draft={false}
            value={(g as Record<string, number>)[p.name] ?? GEM_SCALAR_BASELINE[p.name]}
            onChange={(v) => void apply(r.setGemUniform(p.name, v))}
          />
        ))}

        <SectionLabel>Boost Factors (R/G/B)</SectionLabel>
        <NumberField label="Boost R" min={0} max={4} step={0.00001} draft={false}
          value={g.boostFactors?.[0] ?? GEM_BOOST_BASELINE[0]} onChange={vec("boostFactors", "x")} />
        <NumberField label="Boost G" min={0} max={4} step={0.00001} draft={false}
          value={g.boostFactors?.[1] ?? GEM_BOOST_BASELINE[1]} onChange={vec("boostFactors", "y")} />
        <NumberField label="Boost B" min={0} max={4} step={0.00001} draft={false}
          value={g.boostFactors?.[2] ?? GEM_BOOST_BASELINE[2]} onChange={vec("boostFactors", "z")} />

        <SectionLabel>Colour / Tint</SectionLabel>
        <NumberField label="Color R" min={0} max={2} step={0.00001} draft={false}
          value={g.color?.[0] ?? GEM_COLOR_BASELINE[0]} onChange={vec("color", "x")} />
        <NumberField label="Color G" min={0} max={2} step={0.00001} draft={false}
          value={g.color?.[1] ?? GEM_COLOR_BASELINE[1]} onChange={vec("color", "y")} />
        <NumberField label="Color B" min={0} max={2} step={0.00001} draft={false}
          value={g.color?.[2] ?? GEM_COLOR_BASELINE[2]} onChange={vec("color", "z")} />
        <NumberField label="Opacity" min={0} max={1} step={0.00001} draft={false}
          value={g.opacity ?? GEM_SCALAR_BASELINE.opacity}
          onChange={(v) => void apply(r.setGemOpacity(v))} />

        <div className="mt-1 flex gap-2">
          <Button variant="outline" full onClick={handleExport}>Export JSON</Button>
          <FileButton accept="application/json,.json" full onFile={handleImport}>Import JSON</FileButton>
        </div>
      </Disclosure>
    </div>
  );
}
